import React from 'react'
import { useLDFlagsWithDevOverrides } from '@hooks/useLaunchDarkly'
import { ExperienceFragment } from './ExperienceFragment/ExperienceFragment'
import { withBase } from '@utils/basePath'
import './SiteFooter.scss'

// Fallback Footer component
const Footer: React.FC = () => (
  <div className="fallback-footer">
    <div className="footer-content">
      <div className="footer-links">
        <a href={withBase('/terms')}>Terms & Conditions</a>
        <a href={withBase('/privacy')}>Privacy Policy</a>
        <a href={withBase('/contact')}>Contact Us</a>
      </div>
      <div className="footer-copyright">© {new Date().getFullYear()} Travel + Leisure Co. All rights reserved.</div>
    </div>
  </div>
)

export const SiteFooter: React.FC<{ brand: string; minimal?: boolean }> = React.memo(({ brand, minimal = false }) => {
  const { switchAemFragments } = useLDFlagsWithDevOverrides<any>()
  const [hasError, setHasError] = React.useState(false)
  const fragmentRef = React.useRef<HTMLDivElement>(null)
  const observerRef = React.useRef<MutationObserver | null>(null)

  // Reset error state whenever the brand changes or when AEM fragments are toggled on
  React.useEffect(() => {
    setHasError(false)
  }, [brand])

  React.useEffect(() => {
    if (switchAemFragments) {
      // When the flag flips on (e.g., via user-targeted rule), clear prior error and allow fragment to (re)load
      setHasError(false)
    }
  }, [switchAemFragments])

  // Check if the experience fragment failed to load
  React.useEffect(() => {
    // Clean up any existing observer first
    if (observerRef.current) {
      observerRef.current.disconnect()
      observerRef.current = null
    }

    const checkFragmentContent = () => {
      const fragmentElement = fragmentRef.current
      if (fragmentElement) {
        // Look for the experience fragment element
        const experienceFragment = fragmentElement.querySelector('[data-experience-fragment]')

        if (experienceFragment) {
          const content = experienceFragment.innerHTML.trim()
          // Check if content is meaningful (not loading, not empty)
          const hasMeaningfulContent = content !== '' && content !== 'Loading...'

          if (!hasMeaningfulContent) {
            if (process.env.NODE_ENV === 'development') {
              console.warn('[SiteFooter] Experience fragment failed to load - showing fallback footer')
            }
            setHasError(true)
          }
        }
      }
    }

    // Set up MutationObserver to watch for content changes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          // Check content after DOM changes
          checkFragmentContent()
        }
      })
    })

    observerRef.current = observer

    const fragmentElement = fragmentRef.current
    if (fragmentElement) {
      observer.observe(fragmentElement, {
        childList: true,
        subtree: true,
      })
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect()
        observerRef.current = null
      }
    }
  }, [brand])

  if (hasError) {
    return (
      <footer className="site-footer">
        <Footer />
      </footer>
    )
  }

  return (
    <footer className={minimal ? 'site-footer site-footer--minimal' : 'site-footer'}>
      <div ref={fragmentRef}>
        <ExperienceFragment
          brand={brand}
          name="footer"
          className="aem-footer"
        />
      </div>
    </footer>
  )
})
